import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import { FaFileAlt, FaPen, FaCalculator, FaFileExcel, FaChalkboard, FaCalendarAlt, FaBook, FaSearch } from "react-icons/fa";
import { fetchPrograms } from "../../redux/program/programActions";
import { getClassResultJson } from "../../services/ResultService";
import { getClassWiseSubjects } from "../../services/SubjectServices";
import { termOptions, getTermLabel } from "../../utils/helper";
import Select from "../../components/Select/Select";
import MultiSelect from "../../components/Select/MultiSelect";
import Button from "../../components/Button/Button";
import Loader from "../../components/Loader/Loader";
import Modal from "../../components/Modal/Modal";
import Table from "../../components/Table/Table";
import { viewResultTableConstants } from "./ViewResultTableConstant";
import "./ViewResult.css";

const extractExcelErrorMessage = (error) => {
  const data = error?.response?.data;
  if (data instanceof ArrayBuffer) {
    try {
      const parsed = JSON.parse(new TextDecoder().decode(data));
      return parsed?.message || parsed?.error || "Something went wrong while downloading the Excel file";
    } catch {
      return "Something went wrong while downloading the Excel file";
    }
  }
  return data?.message?.errors || data?.message?.success || data?.message || "Something went wrong while downloading the Excel file";
};

const ViewResultManage = () => {
  const [program_id, setProgramId] = useState("");
  const [term_id, setTermId] = useState("");
  const [markingType, setMarkingType] = useState("actual");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [subjectData, setSubjectData] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [resultData, setResultData] = useState(null);
  const [activeSectionKey, setActiveSectionKey] = useState(null);
  const [eventsModal, setEventsModal] = useState({ open: false, student: null, subject: null });

  const dispatch = useDispatch();
  const programData = useSelector((state) => state.program.programs.data);

  useEffect(() => {
    dispatch(fetchPrograms());
  }, [dispatch]);

  const changeHandler = (e) => {
    const { name, value } = e.target;
    if (name === "program_id") {
      setProgramId(value);
      setSubjectData([]);
      setSelectedSubjects([]);
      fetchSubjects(value);
    } else if (name === "term_id") {
      setTermId(value);
    }
  };

  const fetchSubjects = async (classId) => {
    if (!classId) return;
    try {
      const response = await getClassWiseSubjects(classId);
      setSubjectData(response.data?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubjectSelect = (values) => {
    setSelectedSubjects((values || []).map((value) => value.id));
  };

  const handleMarkingTypeChange = (type) => {
    if (type === markingType) return;
    setMarkingType(type);
    if (program_id && term_id) {
      fetchResult(type);
    }
  };

  const fetchResult = async (type = markingType) => {
    if (!program_id || !term_id) {
      enqueueSnackbar("Please select both Class and Term", { variant: "warning" });
      return;
    }

    try {
      setLoading(true);
      setResultData(null);
      const response = await getClassResultJson(program_id, term_id, type, false, selectedSubjects);

      if (response.status === 200 && response.data?.success) {
        const data = response.data.data;
        setResultData(data);
        const firstSectionKey = Object.keys(data?.sections || {})[0] || null;
        setActiveSectionKey(firstSectionKey);
      } else {
        enqueueSnackbar(response.data?.message?.success || "Unable to fetch result", { variant: "error" });
      }
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message?.errors ||
        error?.response?.data?.message?.success ||
        "Something went wrong while fetching result";
      enqueueSnackbar(apiMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const downloadExcelResult = async () => {
    if (!program_id || !term_id) {
      enqueueSnackbar("Please select both Class and Term", { variant: "warning" });
      return;
    }

    try {
      setDownloading(true);
      const response = await getClassResultJson(program_id, term_id, markingType, true, selectedSubjects);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const className =
        resultData?.class?.class_name ||
        programData.find((program) => String(program.id) === String(program_id))?.name ||
        program_id;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${className}-term-${term_id}-${markingType}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      enqueueSnackbar(extractExcelErrorMessage(error), { variant: "error" });
    } finally {
      setDownloading(false);
    }
  };

  const activeSection = useMemo(() => {
    if (!activeSectionKey || !resultData?.sections) return null;
    return resultData.sections[activeSectionKey];
  }, [activeSectionKey, resultData]);

  const subjectColumns = useMemo(() => {
    if (!activeSection?.students?.length) return [];
    const seen = new Map();
    activeSection.students.forEach((student) => {
      (student.subjects || []).forEach((subject) => {
        if (!seen.has(subject.subject_name)) {
          seen.set(subject.subject_name, subject.max_score);
        }
      });
    });
    const columns = Array.from(seen, ([subject_name, max_score]) => ({ subject_name, max_score }));

    // The JSON endpoint doesn't always honor subject_ids the way the Excel
    // export does, so re-apply the selection on the client as a safety net.
    if (!selectedSubjects.length) return columns;
    const selectedNames = new Set(
      subjectData
        .filter((subject) => selectedSubjects.includes(subject.id))
        .map((subject) => subject.name)
    );
    return columns.filter((column) => selectedNames.has(column.subject_name));
  }, [activeSection, selectedSubjects, subjectData]);

  const openEventsModal = (student, subject) => setEventsModal({ open: true, student, subject });
  const closeEventsModal = () => setEventsModal({ open: false, student: null, subject: null });

  const cols = useMemo(
    () => viewResultTableConstants(subjectColumns, openEventsModal),
    [subjectColumns]
  );

  const sectionEntries = Object.entries(resultData?.sections || {});

  return (
    <div className="card view-result-page">
      <div className="card-header">
        <div className="main-title-page page-title d-flex align-items-center">
          <div className="page-title-icon mx-2">
            <FaFileAlt />
          </div>
          <span>View Result</span>
        </div>
      </div>

      <div className="row card-body">
        <div className="col-12">
          <div className="filter-panel">
            <div className="row g-3">
              <div className="col-md-6">
                <Select
                  label={<><FaChalkboard className="field-icon" /> Class</>}
                  name="program_id"
                  placeholder="Select Class"
                  value={program_id}
                  options={programData}
                  onChangeHandler={changeHandler}
                />
              </div>
              <div className="col-md-6">
                <Select
                  label={<><FaCalendarAlt className="field-icon" /> Term</>}
                  name="term_id"
                  placeholder="Select Term"
                  value={term_id}
                  options={termOptions}
                  onChangeHandler={changeHandler}
                />
              </div>
              <div className="col-md-9">
                <MultiSelect
                  label={<><FaBook className="field-icon" /> Subject</>}
                  options={subjectData}
                  selectedValues={selectedSubjects}
                  onChangeHandler={handleSubjectSelect}
                  closeOnSelect={false}
                  displayValue="name"
                  placeholder={program_id ? "All subjects" : "Select a class first"}
                  disabled={!program_id}
                />
              </div>
              <div className="col-md-3 d-flex align-items-end">
                <Button className="btn btn-primary w-100" onClick={() => fetchResult()} disabled={loading}>
                  <FaSearch className="me-2" /> Search
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 marking-controls-row d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div className="marking-type-tabs">
            <button
              type="button"
              className={`marking-tab${markingType === "actual" ? " active" : ""}`}
              onClick={() => handleMarkingTypeChange("actual")}
            >
              <FaPen className="me-2" /> Actual Marking
            </button>
            <button
              type="button"
              className={`marking-tab${markingType === "calculated" ? " active" : ""}`}
              onClick={() => handleMarkingTypeChange("calculated")}
            >
              <FaCalculator className="me-2" /> Calculated Marking
            </button>
          </div>

          <Button
            className="btn btn-success"
            onClick={downloadExcelResult}
            disabled={downloading || !program_id || !term_id}
          >
            <FaFileExcel className="me-2" /> {downloading ? "Downloading..." : "Download Excel"}
          </Button>
        </div>

        {loading && <Loader />}

        {!loading && resultData && (
          <div className="col-12 results-panel mt-2">
            <div className="d-flex flex-wrap align-items-center view-result-summary mb-3">
              <span className="summary-chip"><strong>Class:</strong> {resultData.class?.class_name}</span>
              <span className="summary-chip"><strong>Term:</strong> {getTermLabel(resultData.term_id) || resultData.term_id}</span>
              <span className="summary-chip"><strong>Total Students:</strong> {activeSection?.students?.length ?? 0}</span>
            </div>

            {sectionEntries.length > 1 && (
              <div className="section-tabs mb-3">
                {sectionEntries.map(([key, section]) => (
                  <Button
                    key={key}
                    className={`btn me-2 ${activeSectionKey === key ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setActiveSectionKey(key)}
                  >
                    {section.label || key}
                  </Button>
                ))}
              </div>
            )}

            {activeSection?.students?.length ? (
              <Table cols={cols} data={activeSection.students} fixedStartCols={2} />
            ) : (
              <div className="d-flex justify-content-center">
                <h5>No data found</h5>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={eventsModal.open}
        onClose={closeEventsModal}
        size="lg"
        title={
          eventsModal.subject
            ? `${eventsModal.subject.subject_name} - ${eventsModal.student?.name}`
            : ""
        }
      >
        {eventsModal.subject && (
          <table className="table table-bordered table-sm mb-0">
            <thead className="table-light">
              <tr>
                <th>Event</th>
                <th>Evaluation Type</th>
                <th className="text-center">Marks</th>
                <th>Remark</th>
              </tr>
            </thead>
            <tbody>
              {(eventsModal.subject.events || []).map((event, idx) => (
                <tr key={event.event_marking_id || idx}>
                  <td>{event.event_name}</td>
                  <td>{event.evaluation_type}</td>
                  <td className="text-center">
                    {event.option_value}
                    {event.event_max_score ? ` / ${event.event_max_score}` : ""}
                  </td>
                  <td>{event.remark || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal>
    </div>
  );
};

export default ViewResultManage;
